import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import { useToast } from '../hooks/useToast';
import { WalletContext } from '../WalletContext';
import {
  createOrder,
  checkPaymentStatus,
  storeOrderInLocalStorage,
  getPendingOrderFromLocalStorage,
  clearPendingOrderFromLocalStorage,
} from '../services/paymentService';
import './ClearPEPayment.css';

function ClearPEPayment({ userId, minimumDeposit = 1, onPaymentSuccess, onPaymentFailed }) {
  const [amount, setAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null); // null, pending, success, failed
  const [pollingActive, setPollingActive] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');

  const navigate = useNavigate();
  const toast = useToast();
  const { updateWalletBalance } = useContext(WalletContext);

  const MINIMUM_AMOUNT = minimumDeposit;
  const POLLING_INTERVAL = 5000; // 5 seconds

  // Check for pending payment on component mount
  useEffect(() => {
    const pendingOrder = getPendingOrderFromLocalStorage();
    if (pendingOrder) {
      setCurrentOrderId(pendingOrder.orderId);
      setAmount(String(pendingOrder.amount));
      setPaymentStatus('pending');
      setStatusMessage(`Checking payment status for ₹${pendingOrder.amount}...`);
      startPolling(pendingOrder.orderId);
    }
  }, []);

  // Polling effect
  useEffect(() => {
    if (!pollingActive || !currentOrderId) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await checkPaymentStatus(currentOrderId);

        if (response.success) {
          // Success
          setPaymentStatus('success');
          setStatusMessage('✓ Payment Successful! Your wallet has been credited.');
          setPollingActive(false);
          clearPendingOrderFromLocalStorage();

          // Show success toast
          toast.success(`Amount ₹${amount} added successfully`);

          // Update wallet balance
          if (userId) {
            updateWalletBalance(userId);
          }

          // Call callback
          onPaymentSuccess?.({
            orderId: currentOrderId,
            success: true,
          });

          // Redirect to Add Funds page after delay
          setTimeout(() => {
            navigate('/addfunds');
          }, 2000);
        } else if (response.success === false) {
          // Failed
          setPaymentStatus('failed');
          setStatusMessage('✗ Payment Failed. Please try again.');
          setPollingActive(false);
          clearPendingOrderFromLocalStorage();

          // Show failure toast
          toast.error('Payment Failed something went wrong');

          // Call callback
          onPaymentFailed?.({
            orderId: currentOrderId,
            success: false,
          });

          // Redirect to Add Funds page after delay
          setTimeout(() => {
            navigate('/addfunds');
          }, 2000);
        }
        // If success is not defined: pending, continue polling
      } catch (err) {
        console.error('Polling error:', err);
        // Continue polling even on error
      }
    }, POLLING_INTERVAL);

    return () => clearInterval(pollInterval);
  }, [pollingActive, currentOrderId, onPaymentSuccess, onPaymentFailed, amount, userId, navigate, toast, updateWalletBalance]);

  const startPolling = (orderId) => {
    setPollingActive(true);
  };

  const validateAmount = (value) => {
    const numValue = Number(value);
    if (!value || numValue === 0) {
      setError('Please enter an amount');
      return false;
    }
    if (numValue < MINIMUM_AMOUNT) {
      setError(`Minimum amount is ₹${MINIMUM_AMOUNT}`);
      return false;
    }
    return true;
  };

  const handlePaymentInitiation = async () => {
    setError('');

    // Clear pendingPaymentOrder from localStorage
    localStorage.removeItem('pendingPaymentOrder');

    if (!validateAmount(amount)) {
      return;
    }

    if (!userId) {
      setError('User ID is missing. Please log in again.');
      return;
    }

    setIsLoading(true);
    setPaymentStatus('pending');

    try {
      const numAmount = Number(amount);
      const response = await createOrder(userId, numAmount);

      if (response.success && response.payment_url) {
        const { payment_url, txn_id } = response;
        const orderId = response.order_id || txn_id; // Use order_id if available, fallback to txn_id

        // Store in localStorage for polling
        storeOrderInLocalStorage(orderId, txn_id, numAmount);
        setCurrentOrderId(orderId);

        setStatusMessage(
          'Redirecting to ClearPE payment gateway...'
        );

        // Start polling before redirect
        startPolling(orderId);

        // Redirect to payment URL
        // Add small delay to ensure state is updated
        setTimeout(() => {
          window.location.href = payment_url;
        }, 500);
      } else {
        throw new Error(response.message || 'Failed to create payment order');
      }
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      setPaymentStatus(null);
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    setError('');
    setPaymentStatus(null);
    setStatusMessage('');
    setCurrentOrderId(null);
    setAmount('');
  };

  // Render based on payment status
  if (paymentStatus === 'success') {
    return (
      <div className="clearpe-payment-container">
        <div className="success-state">
          <div className="success-icon">✓</div>
          <h2>Payment Successful!</h2>
          <p className="success-message">{statusMessage}</p>
          <p className="amount-text">Amount: ₹{amount}</p>
          <button className="action-btn primary" onClick={handleRetry}>
            Close
          </button>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'failed') {
    return (
      <div className="clearpe-payment-container">
        <div className="failed-state">
          <div className="failed-icon">✗</div>
          <h2>Payment Failed</h2>
          <p className="error-message">{statusMessage}</p>
          <p className="amount-text">Amount: ₹{amount}</p>
          <button className="action-btn primary" onClick={handleRetry}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'pending' && pollingActive) {
    return (
      <div className="clearpe-payment-container">
        <LoadingSpinner />
        <div className="polling-message">
          <p>{statusMessage}</p>
        </div>
      </div>
    );
  }

  // Default: Payment form
  return (
    <div className="clearpe-payment-container">
      <div className="payment-form">
        <h2>Add Funds via ClearPE</h2>

        {error && <div className="error-alert">{error}</div>}

        <div className="form-group">
          <label htmlFor="amount">Amount (₹)</label>
          <input
            id="amount"
            type="number"
            className="amount-input"
            placeholder="Enter amount"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setError('');
            }}
            min={MINIMUM_AMOUNT}
            disabled={isLoading}
          />
          <small className="input-hint">Minimum: ₹{MINIMUM_AMOUNT}</small>
        </div>

        <div className="info-box">
          <p>✓ Secure payment gateway</p>
          <p>✓ Instant credit to your wallet</p>
          <p>✓ 100% safe and encrypted</p>
        </div>

        <button
          className={`pay-btn ${isLoading ? 'loading' : ''}`}
          onClick={handlePaymentInitiation}
          disabled={isLoading || !amount}
        >
          {isLoading ? 'Processing...' : 'Proceed to Add'}
        </button>

        {isLoading && <LoadingSpinner />}
      </div>
    </div>
  );
}

export default ClearPEPayment;
